import React, { useState } from 'react';
import { Search, Calendar, Phone, Hash, Globe, Smartphone, ChevronRight, Filter } from 'lucide-react';
import { Order, OrderStatus } from './OrderQueue';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderHistoryProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
export function OrderHistory({
  orders,
  onSelectOrder
}: OrderHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const filteredOrders = orders.filter(order => {
    const isTerminalStatus = ['Delivered', 'Cancelled', 'Rejected', 'Reassigned', 'Return Requested', 'Refunded'].includes(order.status);
    if (!isTerminalStatus) return false;
    const matchesId = order.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customer.mobile.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    return matchesId && matchesPhone && matchesSource;
  });
  return <div className="space-y-6" data-id="element-617" data-mp="2jr3fb6a6fclm">
      <div className="flex items-center justify-between" data-id="element-618" data-mp="2jqbhrpx0r4sp">
        <div data-id="element-619" data-mp="2joqoe3mfxrwf">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-620" data-mp="25rllgci0x95e">Order History</h2>
          <p className="text-slate-500" data-id="element-621" data-mp="25tolzqgsykod">Search and review past order logs</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-4 gap-4" data-id="element-622" data-mp="1wqx6nfkt34rg">
        <div className="space-y-2" data-id="element-623" data-mp="20rphgd9bthpu">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-624" data-mp="20sr3fcrvromc">
            <Hash className="w-3 h-3" data-id="element-625" data-mp="3r5g6fadyqu5s" />
            Order ID
          </label>
          <div className="relative" data-id="element-626" data-mp="3v4yw0jgqbtey">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-627" data-mp="3v3xgo2sokkrf" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-628" data-mp="17vg9rssiklfw" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-629" data-mp="1qq4yd4vykj8w">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-630" data-mp="1mmgqsm7btnnn">
            <Phone className="w-3 h-3" data-id="element-631" data-mp="1huysk5wou16d" />
            Customer Phone
          </label>
          <div className="relative" data-id="element-632" data-mp="1cxp5gwwru1lp">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-633" data-mp="1cwnfznf0dckg" />
            <input type="text" value={phoneQuery} onChange={e => setPhoneQuery(e.target.value)} placeholder="050..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-634" data-mp="2adun138lm3dw" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-635" data-mp="2mtc45osz2t03">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-636" data-mp="2mx9mw9bn1ijg">
            <Globe className="w-3 h-3" data-id="element-637" data-mp="2vvv3w01gopi4" />
            Source
          </label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2 appearance-none" data-id="element-638" data-mp="2vtr33qyv06m2">
            <option value="all" data-id="element-639" data-mp="3amauxvgk6ro7">All Sources</option>
            <option value="Website" data-id="element-640" data-mp="3ene644nnvzj3">Website</option>
            <option value="Aggregator" data-id="element-641" data-mp="3jl5yt0w4rpoc">Aggregator</option>
            <option value="Yaser eRx" data-id="element-642" data-mp="3jlpf6d64u609">Yaser eRx</option>
            <option value="Walk-in" data-id="element-643" data-mp="35xxgkpde87wy">Walk-in</option>
            <option value="Insurance Platform" data-id="element-644" data-mp="35wmf4w7f7c90">Insurance Platform</option>
          </select>
        </div>

        <div className="space-y-2" data-id="element-645" data-mp="31ure7stej0vr">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-646" data-mp="3fnz33cq3li9r">
            <Calendar className="w-3 h-3" data-id="element-647" data-mp="3aovy1sa4bc1s" />
            Date
          </label>
          <input type="date" className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-648" data-mp="2mzbeb7654arg" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-649" data-mp="2wz2bjgm353v8">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-650" data-mp="2rxcw7orty41c">
          <div data-id="element-651" data-mp="77xav9bdr7j3">Order ID</div>
          <div data-id="element-652" data-mp="7at8ewjbfu70">Customer</div>
          <div data-id="element-653" data-mp="1p5k8zsgrglot">Source</div>
          <div data-id="element-654" data-mp="1p5afvw0f5yew">Status</div>
          <div data-id="element-655" data-mp="1p4qzwkubxk3i">Date</div>
          <div data-id="element-656" data-mp="1u558c3zuew22">Amount</div>
          <div className="w-10" data-id="element-657" data-mp="1u5eybrkw137r"></div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-658" data-mp="1u327sq12dkj3">
          {filteredOrders.map(order => <button key={order.id} onClick={() => onSelectOrder(order)} className="w-full grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-5 items-center hover:bg-slate-50 transition-colors text-left group" data-id="element-659" data-mp="1g7gnux9otqxj">
              <div className="font-mono font-bold text-blue-600" data-id="element-660" data-mp="2cv4t4eonhozo">
                #{order.id}
              </div>

              <div data-id="element-661" data-mp="1z25e0dss5wsn">
                <p className="font-semibold text-slate-900" data-id="element-662" data-mp="243cm6dyu4xzu">
                  {order.customer.name}
                </p>
                <p className="text-xs text-slate-500" data-id="element-663" data-mp="2rsdlnbpowysz">{order.customer.mobile}</p>
              </div>

              <div className="flex items-center gap-2" data-id="element-664" data-mp="2wk5ho1rm7wqi">
                <div className={cn('flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', order.channel === 'Aggregator' ? 'bg-orange-50 text-orange-700' : order.channel === 'Walk-in' ? 'bg-purple-50 text-purple-700' : 'bg-indigo-50 text-indigo-700')} data-id="element-665" data-mp="2wlqbtqa0pdnu">
                  {order.channel === 'Aggregator' ? <Smartphone className="h-3 w-3" data-id="element-666" data-mp="2rid8zsswmihz" /> : <Globe className="h-3 w-3" data-id="element-667" data-mp="2rj4w0f8fr7wn" />}
                  {order.channel}
                </div>
              </div>

              <div data-id="element-668" data-mp="2nib5rf7ith0l">
                <StatusBadge status={order.status} data-id="element-669" data-mp="2niupn1gqefh9" />
              </div>

              <div className="text-sm text-slate-600 font-medium" data-id="element-670" data-mp="2ip9pp3vkvbff">
                {order.createdAt}
              </div>

              <div data-id="element-671" data-mp="2douas7vndvvc">
                <p className="font-bold text-slate-900" data-id="element-672" data-mp="28rvjry7a651d">
                  {order.totalAmount.toFixed(2)} SAR
                </p>
                <p className={cn('text-[10px] font-bold uppercase', order.paymentType === 'COD' ? 'text-amber-600' : 'text-emerald-600')} data-id="element-673" data-mp="23t0r84jih242">
                  {order.paymentType}
                </p>
              </div>

              <div className="flex items-center gap-3" data-id="element-674" data-mp="jckszq9af46g">
                <div className="text-slate-300 group-hover:text-blue-600 transition-colors" data-id="element-675" data-mp="ob4h9xbcss1z">
                  <ChevronRight className="w-5 h-5" data-id="element-676" data-mp="t7uxckn5rnne" />
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<string, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100',
    'Awaiting Partner Courier': 'bg-cyan-50 text-cyan-700 border-cyan-100',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    Rejected: 'bg-red-50 text-red-700 border-red-100',
    Reassigned: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    'Awaiting Payment': 'bg-amber-50 text-amber-700 border-amber-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100'
  };
  return <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border', styles[status] || 'bg-slate-50 text-slate-700 border-slate-100')} data-id="element-677" data-mp="3927sxi9c0htk">
      {status}
    </div>;
}